import { Stack, Title, Text, Group, Button, Paper, TextInput, ThemeIcon, Slider, Box, Chip } from '@mantine/core';
import { IconWorld, IconArrowRight, IconArrowLeft } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { FoodAutocompleteInput } from '../UI/FoodAutocompleteInput';
import { searchIngredients } from '../../data/ingredientDatabase';
import { HouseholdSegment, bySegment } from '../../utils/householdSegment';

type CuisinePreferencesStepProps = {
  value: { cuisinePreferences: string[]; avoidCuisines: string[]; dislikes: string[]; cuisineAdventure?: number };
  onChange: (value: { cuisinePreferences: string[]; avoidCuisines: string[]; dislikes: string[]; cuisineAdventure?: number }) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isCompleting?: boolean;
  householdSegment?: HouseholdSegment;
};

const CUISINE_TYPES = [
  { name: 'Italian', flag: '🇮🇹' },
  { name: 'Chinese', flag: '🇨🇳' },
  { name: 'Japanese', flag: '🇯🇵' },
  { name: 'Indian', flag: '🇮🇳' },
  { name: 'Mexican', flag: '🇲🇽' },
  { name: 'French', flag: '🇫🇷' },
  { name: 'Thai', flag: '🇹🇭' },
  { name: 'American', flag: '🇺🇸' },
  { name: 'Korean', flag: '🇰🇷' },
  { name: 'Vietnamese', flag: '🇻🇳' },
  { name: 'Latin', flag: '🌎  ' },
  { name: 'African', flag: '🌍  ' },
  { name: 'Mediterranean', flag: '🏛️  ' },
  { name: 'Middle Eastern', flag: '🏺  ' },
];

export const CuisinePreferencesStep = ({ value, onChange, onNext, onBack, isLastStep, householdSegment }: CuisinePreferencesStepProps) => {
  const [otherCuisineInput, setOtherCuisineInput] = useState('');
  const [avoidCuisineInput, setAvoidCuisineInput] = useState('');
  const [dislikeInput, setDislikeInput] = useState('');
  const [showAvoidCuisines, setShowAvoidCuisines] = useState(false);
  const [noAvoidCuisines, setNoAvoidCuisines] = useState(false);
  const [pendingNext, setPendingNext] = useState(false);

  // Initialize showAvoidCuisines based on existing data
  useEffect(() => {
    if ((value.avoidCuisines || []).length > 0) {
      setShowAvoidCuisines(true);
    }
  }, [value.avoidCuisines]);

  // Initialize default cuisineAdventure value when slider becomes visible
  useEffect(() => {
    if (value.cuisinePreferences.length > 0 && value.cuisineAdventure === undefined) {
      onChange({ ...value, cuisineAdventure: 50 });
    }
  }, [value.cuisinePreferences.length, value.cuisineAdventure, value, onChange]);

  // Effect to call onNext after state has been updated
  useEffect(() => {
    if (pendingNext) {
      setPendingNext(false);
      onNext();
    }
  }, [value.cuisinePreferences, value.avoidCuisines, value.dislikes, pendingNext, onNext]);

  const toggleFavoriteCuisine = (cuisine: string) => {
    const currentFavorites = value.cuisinePreferences;
    const currentAvoids = value.avoidCuisines || [];
    
    const updatedFavorites = currentFavorites.includes(cuisine)
      ? currentFavorites.filter(c => c !== cuisine)
      : [...currentFavorites, cuisine];
    
    // Remove from avoid list if adding to favorites (case-insensitive)
    const updatedAvoids = updatedFavorites.includes(cuisine)
      ? currentAvoids.filter(c => c.toLowerCase() !== cuisine.toLowerCase())
      : currentAvoids;
    
    onChange({ ...value, cuisinePreferences: updatedFavorites, avoidCuisines: updatedAvoids });
  };

  const toggleAvoidCuisine = (cuisine: string) => {
    const currentAvoids = value.avoidCuisines || [];
    const currentFavorites = value.cuisinePreferences;
    
    const updatedAvoids = currentAvoids.includes(cuisine)
      ? currentAvoids.filter(c => c !== cuisine)
      : [...currentAvoids, cuisine];
    
    // Remove from favorites list if adding to avoid (case-insensitive)
    const updatedFavorites = updatedAvoids.includes(cuisine)
      ? currentFavorites.filter(c => c.toLowerCase() !== cuisine.toLowerCase())
      : currentFavorites;
    
    onChange({ ...value, avoidCuisines: updatedAvoids, cuisinePreferences: updatedFavorites });
  };

  const addCustomFavoriteCuisine = () => {
    if (!otherCuisineInput.trim()) return;
    
    const predefinedNames = CUISINE_TYPES.map(c => c.name);
    
    // Split by comma and process each item
    const newCuisines = otherCuisineInput
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
      .filter(item => {
        // Check against existing favorites (case-insensitive)
        const alreadyInFavorites = value.cuisinePreferences.some(c => c.toLowerCase() === item.toLowerCase());
        // Check against predefined cuisines (case-insensitive)
        const isPredefined = predefinedNames.some(p => p.toLowerCase() === item.toLowerCase());
        return !alreadyInFavorites && !isPredefined;
      });
    
    // Handle predefined cuisines by adding them directly
    const predefinedToAdd = otherCuisineInput
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => predefinedNames.find(p => p.toLowerCase() === item.toLowerCase()))
      .filter((cuisine): cuisine is string => Boolean(cuisine))
      .filter(cuisine => !value.cuisinePreferences.includes(cuisine));
    
    if (newCuisines.length === 0 && predefinedToAdd.length === 0) {
      setOtherCuisineInput('');
      return;
    }
    
    const currentFavorites = value.cuisinePreferences;
    const currentAvoids = value.avoidCuisines || [];
    
    const allNewCuisines = [...predefinedToAdd, ...newCuisines];
    const updatedFavorites = [...currentFavorites, ...allNewCuisines];
    // Remove from avoid list if adding to favorites (case-insensitive)
    const updatedAvoids = currentAvoids.filter(avoid => 
      !allNewCuisines.some(newCuisine => newCuisine.toLowerCase() === avoid.toLowerCase())
    );
    
    onChange({ ...value, cuisinePreferences: updatedFavorites, avoidCuisines: updatedAvoids });
    setOtherCuisineInput('');
  };

  const addCustomAvoidCuisine = () => {
    if (!avoidCuisineInput.trim()) return;
    
    const predefinedNames = CUISINE_TYPES.map(c => c.name);
    
    // Split by comma and process each item
    const newAvoidCuisines = avoidCuisineInput
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
      .filter(item => {
        // Check against existing avoids (case-insensitive)
        const alreadyInAvoids = (value.avoidCuisines || []).some(c => c.toLowerCase() === item.toLowerCase());
        // Check against predefined cuisines (case-insensitive)
        const isPredefined = predefinedNames.some(p => p.toLowerCase() === item.toLowerCase());
        return !alreadyInAvoids && !isPredefined;
      });
    
    // Handle predefined cuisines by adding them directly
    const predefinedToAdd = avoidCuisineInput
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => predefinedNames.find(p => p.toLowerCase() === item.toLowerCase()))
      .filter((cuisine): cuisine is string => Boolean(cuisine))
      .filter(cuisine => !(value.avoidCuisines || []).includes(cuisine));
    
    if (newAvoidCuisines.length === 0 && predefinedToAdd.length === 0) {
      setAvoidCuisineInput('');
      return;
    }
    
    const currentAvoids = value.avoidCuisines || [];
    const currentFavorites = value.cuisinePreferences;
    
    const allNewAvoidCuisines = [...predefinedToAdd, ...newAvoidCuisines];
    const updatedAvoids = [...currentAvoids, ...allNewAvoidCuisines];
    // Remove from favorites list if adding to avoid (case-insensitive)
    const updatedFavorites = currentFavorites.filter(favorite => 
      !allNewAvoidCuisines.some(newAvoid => newAvoid.toLowerCase() === favorite.toLowerCase())
    );
    
    onChange({ ...value, avoidCuisines: updatedAvoids, cuisinePreferences: updatedFavorites });
    setAvoidCuisineInput('');
  };

  const removeFavoriteCuisine = (cuisine: string) => {
    const updated = value.cuisinePreferences.filter(c => c !== cuisine);
    onChange({ ...value, cuisinePreferences: updated });
  };

  const removeAvoidCuisine = (cuisine: string) => {
    const updated = (value.avoidCuisines || []).filter(c => c !== cuisine);
    onChange({ ...value, avoidCuisines: updated });
  };

  const addDislike = (text: string) => {
    if (!text.trim()) return;

    // Split by comma and process each item
    const newDislikes = text
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
      .filter(item => !value.dislikes.some(d => d.toLowerCase() === item.toLowerCase())); // Avoid duplicates (case-insensitive)
    
    if (newDislikes.length === 0) return;
    
    const current = value.dislikes;
    const updated = [...current, ...newDislikes];
    
    onChange({ ...value, dislikes: updated });
    setDislikeInput('');
  };

  const removeDislike = (dislike: string) => {
    const updated = value.dislikes.filter(d => d !== dislike);
    onChange({ ...value, dislikes: updated });
  };

  const selectAllCuisines = () => {
    // Add all predefined cuisines that aren't already selected and aren't in the avoid list (case-insensitive)
    const avoidCuisinesLower = (value.avoidCuisines || []).map(c => c.toLowerCase());
    const availableCuisines = CUISINE_TYPES.filter(cuisine => !avoidCuisinesLower.includes(cuisine.name.toLowerCase())).map(c => c.name);
    const allCuisines = Array.from(new Set([...value.cuisinePreferences, ...availableCuisines]));
    onChange({ ...value, cuisinePreferences: allCuisines });
  };

  const clearAllCuisines = () => {
    // Keep only custom cuisines (non-predefined ones)
    const predefinedNames = CUISINE_TYPES.map(c => c.name);
    const customOnly = value.cuisinePreferences.filter(c => !predefinedNames.includes(c));
    onChange({ ...value, cuisinePreferences: customOnly });
  };

  const handleAvoidCuisinesToggle = (hasAvoidCuisines: boolean) => {
    if (hasAvoidCuisines) {
      setShowAvoidCuisines(true);
      setNoAvoidCuisines(false);
    } else {
      setNoAvoidCuisines(true);
      setShowAvoidCuisines(false);
      // Clear all avoided cuisines when user says they don't avoid any
      onChange({ ...value, avoidCuisines: [] });
      setAvoidCuisineInput('');
    }
  };

  const handleNext = () => {
    let updated = false;
    let newValue = { ...value };

    // Auto-add text from "Other cuisines" input
    if (otherCuisineInput.trim()) {
      const predefinedNames = CUISINE_TYPES.map(c => c.name);
      const newCuisines = otherCuisineInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
        .filter(item => {
          const alreadyInFavorites = value.cuisinePreferences.some(c => c.toLowerCase() === item.toLowerCase());
          const isPredefined = predefinedNames.some(p => p.toLowerCase() === item.toLowerCase());
          return !alreadyInFavorites && !isPredefined;
        });

      const predefinedToAdd = otherCuisineInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item => predefinedNames.find(p => p.toLowerCase() === item.toLowerCase()))
        .filter((cuisine): cuisine is string => Boolean(cuisine))
        .filter(cuisine => !value.cuisinePreferences.includes(cuisine));

      const allNewCuisines = [...predefinedToAdd, ...newCuisines];
      if (allNewCuisines.length > 0) {
        const currentAvoids = newValue.avoidCuisines || [];
        newValue.cuisinePreferences = [...newValue.cuisinePreferences, ...allNewCuisines];
        newValue.avoidCuisines = currentAvoids.filter(avoid =>
          !allNewCuisines.some(newCuisine => newCuisine.toLowerCase() === avoid.toLowerCase())
        );
        updated = true;
      }
    }

    // Auto-add text from "Other cuisines to avoid" input
    if (avoidCuisineInput.trim()) {
      const predefinedNames = CUISINE_TYPES.map(c => c.name);
      const newAvoidCuisines = avoidCuisineInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
        .filter(item => {
          const alreadyInAvoids = (newValue.avoidCuisines || []).some(c => c.toLowerCase() === item.toLowerCase());
          const isPredefined = predefinedNames.some(p => p.toLowerCase() === item.toLowerCase());
          return !alreadyInAvoids && !isPredefined;
        });

      const predefinedToAdd = avoidCuisineInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item => predefinedNames.find(p => p.toLowerCase() === item.toLowerCase()))
        .filter((cuisine): cuisine is string => Boolean(cuisine))
        .filter(cuisine => !(newValue.avoidCuisines || []).includes(cuisine));

      const allNewAvoidCuisines = [...predefinedToAdd, ...newAvoidCuisines];
      if (allNewAvoidCuisines.length > 0) {
        newValue.avoidCuisines = [...(newValue.avoidCuisines || []), ...allNewAvoidCuisines];
        newValue.cuisinePreferences = newValue.cuisinePreferences.filter(favorite =>
          !allNewAvoidCuisines.some(newAvoid => newAvoid.toLowerCase() === favorite.toLowerCase())
        );
        updated = true;
      }
    }

    // Auto-add text from "Foods to avoid" input
    if (dislikeInput.trim()) {
      const newDislikes = dislikeInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
        .filter(item => !newValue.dislikes.some(d => d.toLowerCase() === item.toLowerCase()));

      if (newDislikes.length > 0) {
        newValue.dislikes = [...newValue.dislikes, ...newDislikes];
        updated = true;
      }
    }

    if (updated) {
      onChange(newValue);
      setPendingNext(true);
      return;
    }

    onNext();
  };

  // Separate predefined and custom cuisines for display
  const predefinedNames = CUISINE_TYPES.map(c => c.name);
  const predefinedFavorites = value.cuisinePreferences.filter(c => predefinedNames.includes(c));
  const customFavorites = value.cuisinePreferences.filter(c => !predefinedNames.includes(c));
  const predefinedAvoids = (value.avoidCuisines || []).filter(c => predefinedNames.includes(c));
  const customAvoids = (value.avoidCuisines || []).filter(c => !predefinedNames.includes(c));

  // Ingredient-catalog suggestions for the "Foods to avoid" input (same catalog
  // that powers pantry autocomplete). Only search once there are 2+ characters,
  // and hide anything already in the list.
  const dislikeSuggestions = dislikeInput.trim().length >= 2
    ? searchIngredients(dislikeInput, 8)
        .map(ing => ing.displayName ?? ing.name)
        .filter(name => !value.dislikes.some(d => d.toLowerCase() === name.toLowerCase()))
    : [];

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
        <Stack gap="md" ta="center" align="center">
          <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
            <IconWorld size={40} />
          </ThemeIcon>
          <Stack gap="xs" align="center">
            <Title order={2} size="h1" fw={700} c="dark">
              Cuisine preferences
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={400}>
              {bySegment(householdSegment, {
                family: 'What types of food does your family enjoy most?',
                household: 'What types of food does your household enjoy most?',
                solo: 'What types of food do you enjoy most?',
              })}
            </Text>
          </Stack>
        </Stack>

      {/* Cuisine Preferences */}
        <Paper 
          radius="lg" 
          p={{ base: 'md', sm: 'xl' }}
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            width: '100%'
          }}
        >
          <Stack gap="xl">
            {/* Cuisine Preferences */}
            <Stack gap="md">
              <div style={{ position: 'relative' }}>
                <div>
                  <Text fw={600} size="lg" c="dark">What cuisines do you like?</Text>
                </div>

                <Button
                  variant="subtle"
                  size="xs"
                  color={predefinedFavorites.length > 0 ? "gray" : "orange"}
                  onClick={predefinedFavorites.length > 0 ? clearAllCuisines : selectAllCuisines}
                  style={{ position: 'absolute', top: '-4px', right: 0 }}
                  visibleFrom="sm"
                >
                  {predefinedFavorites.length > 0 ? "Clear all" : "Select all"}
                </Button>
              </div>
              
              <Group gap="xs" wrap="wrap" className="chip-cloud">
                {CUISINE_TYPES.map((cuisine) => {
                  const isInAvoidList = (value.avoidCuisines || []).some(c => c.toLowerCase() === cuisine.name.toLowerCase());
                  return (
                    <Chip
                      key={cuisine.name}
                      checked={predefinedFavorites.includes(cuisine.name)}
                      onChange={() => toggleFavoriteCuisine(cuisine.name)}
                      variant="outline"
                      color="green"
                      size="sm"
                      disabled={isInAvoidList}
                      style={{
                        minWidth: 'fit-content',
                        opacity: isInAvoidList ? 0.5 : 1
                      }}
                    >
                      {cuisine.flag}<span style={{ marginLeft: '8px' }}>{cuisine.name}</span>
                    </Chip>
                  );
                })}
              </Group>

              {/* Other cuisines input */}
              <Stack gap="xs">
                <Text fw={500} size="sm">Other cuisines</Text>
                <Group gap="xs">
                  <TextInput
                    placeholder="e.g. Caribbean, Moroccan, Peruvian..."
                    value={otherCuisineInput}
                    onChange={(e) => setOtherCuisineInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addCustomFavoriteCuisine()}
                    style={{ flex: 1 }}
                    size="sm"
                  />
                  <Button size="sm" onClick={addCustomFavoriteCuisine} disabled={!otherCuisineInput.trim()} color="green">
                    Add
                  </Button>
                </Group>
                
                {/* Custom cuisines display */}
                {customFavorites.length > 0 && (
                  <Group gap="xs" wrap="wrap" className="chip-cloud">
                    {customFavorites.map(cuisine => (
                      <Chip
                        key={cuisine}
                        checked={true}
                        onChange={() => removeFavoriteCuisine(cuisine)}
                        variant="filled"
                        color="green"
                        size="sm"
                        style={{ minWidth: 'fit-content' }}
                      >
                        {cuisine}
                      </Chip>
                    ))}
                  </Group>
                )}
              </Stack>
            </Stack>

            {/* Cuisines to Avoid */}
            <Stack gap="md">
              <div>
                <Text fw={600} size="lg" c="dark">Any cuisines to avoid?</Text>
              </div>

              {!showAvoidCuisines && !noAvoidCuisines ? (
                <Stack gap="sm">
                  <Group gap="sm">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => handleAvoidCuisinesToggle(true)}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => handleAvoidCuisinesToggle(false)}
                    >
                      No
                    </Button>
                  </Group>
                </Stack>
              ) : showAvoidCuisines ? (
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Text size="sm" c="dimmed">
                      {bySegment(householdSegment, {
                        family: 'Select cuisines your family prefers not to eat',
                        household: 'Select cuisines your household prefers not to eat',
                        solo: 'Select cuisines you prefer not to eat',
                      })}
                    </Text>
                    <Button
                      variant="subtle"
                      size="xs"
                      color="gray"
                      onClick={() => {
                        setShowAvoidCuisines(false);
                        setNoAvoidCuisines(false);
                        onChange({ ...value, avoidCuisines: [] });
                        setAvoidCuisineInput('');
                      }}
                    >
                      Cancel
                    </Button>
                  </Group>
                  
                  {/* Quick selection buttons */}
                  <Group gap="xs" wrap="wrap" className="chip-cloud">
                    {CUISINE_TYPES.map((cuisine) => {
                      const isInFavoritesList = value.cuisinePreferences.some(c => c.toLowerCase() === cuisine.name.toLowerCase());
                      return (
                        <Chip
                          key={cuisine.name}
                          checked={predefinedAvoids.includes(cuisine.name)}
                          onChange={() => toggleAvoidCuisine(cuisine.name)}
                          variant="outline"
                          color="red"
                          size="sm"
                          disabled={isInFavoritesList}
                          style={{
                            minWidth: 'fit-content',
                            opacity: isInFavoritesList ? 0.5 : 1
                          }}
                        >
                          {cuisine.flag}<span style={{ marginLeft: '8px' }}>{cuisine.name}</span>
                        </Chip>
                      );
                    })}
                  </Group>

                  {/* Custom input */}
                  <Stack gap="xs">
                    <Text fw={500} size="sm">Other cuisines to avoid</Text>
                    <Group gap="xs">
                      <TextInput
                        placeholder="e.g. Caribbean, Moroccan, Peruvian..."
                        value={avoidCuisineInput}
                        onChange={(e) => setAvoidCuisineInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addCustomAvoidCuisine()}
                        style={{ flex: 1 }}
                        size="sm"
                      />
                      <Button size="sm" onClick={addCustomAvoidCuisine} disabled={!avoidCuisineInput.trim()} color="red">
                        Add
                      </Button>
                    </Group>
                    
                    {/* Custom avoided cuisines display */}
                    {customAvoids.length > 0 && (
                      <Group gap="xs" wrap="wrap" className="chip-cloud">
                        {customAvoids.map(cuisine => (
                          <Chip
                            key={cuisine}
                            checked={true}
                            onChange={() => removeAvoidCuisine(cuisine)}
                            variant="filled"
                            color="red"
                            size="sm"
                            style={{ minWidth: 'fit-content' }}
                          >
                            {cuisine}
                          </Chip>
                        ))}
                      </Group>
                    )}
                  </Stack>
                </Stack>
              ) : (
                <Stack gap="sm">
                  <Text size="sm" c="green.7">✓ No cuisines to avoid</Text>
                  <Button
                    variant="subtle"
                    size="xs"
                    color="gray"
                    onClick={() => {
                      setNoAvoidCuisines(false);
                    }}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    Change
                  </Button>
                </Stack>
              )}
            </Stack>

            {/* Dislikes */}
            <Stack gap="md">
              <div>
                <Text fw={600} size="lg" c="dark">Foods to avoid</Text>
                <Text size="sm" c="dimmed">
                  {bySegment(householdSegment, {
                    family: "Any specific ingredients or dishes your family doesn't like?",
                    household: "Any specific ingredients or dishes your household doesn't like?",
                    solo: "Any specific ingredients or dishes you don't like?",
                  })}
                </Text>
              </div>
              
              <Stack gap="xs">
                <FoodAutocompleteInput
                  value={dislikeInput}
                  onChange={setDislikeInput}
                  onAdd={addDislike}
                  suggestions={dislikeSuggestions}
                  placeholder="e.g. mushrooms, spicy food, seafood..."
                  color="red"
                />

                {/* Dislikes display */}
                {value.dislikes.length > 0 && (
                  <Group gap="xs" wrap="wrap" className="chip-cloud">
                    {value.dislikes.map(dislike => (
                      <Chip
                        key={dislike}
                        checked={true}
                        onChange={() => removeDislike(dislike)}
                        variant="filled"
                        color="red"
                        size="sm"
                        style={{ minWidth: 'fit-content' }}
                      >
                        {dislike}
                      </Chip>
                    ))}
                  </Group>
                )}
              </Stack>
            </Stack>

            {/* Cuisine Strictness */}
            {value.cuisinePreferences.length > 0 && (
              <Stack gap="md">
                <Text fw={600} size="lg">How adventurous should we be?</Text>
                <Text size="sm" c="dimmed">Tell us how open you are to exploring new flavors</Text>
                
                <Box px="lg">
                  {/* Mobile: Simplified labels */}
                  <Slider
                    value={value.cuisineAdventure ?? 50}
                    onChange={(val) => onChange({ ...value, cuisineAdventure: val })}
                    min={0}
                    max={100}
                    step={25}
                    size="lg"
                    color="orange"
                    hiddenFrom="sm"
                    label={null}
                    marks={[
                      { value: 0, label: 'Safe' },
                      { value: 50, label: 'Balanced' },
                      { value: 100, label: 'Bold' }
                    ]}
                    styles={{
                      thumb: { 
                        border: '2px solid white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      },
                      mark: {
                        fontSize: '11px'
                      }
                    }}
                    my="xl"
                  />
                  
                  {/* Desktop: Full labels */}
                  <Slider
                    value={value.cuisineAdventure ?? 50}
                    onChange={(val) => onChange({ ...value, cuisineAdventure: val })}
                    min={0}
                    max={100}
                    step={25}
                    size="lg"
                    color="orange"
                    visibleFrom="sm"
                    label={null}
                    marks={[
                      { value: 0, label: 'Safe' },
                      { value: 25, label: 'Familiar' },
                      { value: 50, label: 'Balanced' },
                      { value: 75, label: 'Adventurous' },
                      { value: 100, label: 'Bold' }
                    ]}
                    styles={{
                      thumb: { 
                        border: '2px solid white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      },
                      mark: {
                        fontSize: '12px'
                      }
                    }}
                    my="xl"
                  />
                  
                  <Text size="sm" c="dimmed" ta="center" fw={500} mt="md">
                    {value.cuisineAdventure === 0 && "We'll only suggest recipes from your selected cuisines"}
                    {value.cuisineAdventure === 25 && "We'll primarily use your favorites with occasional new options"}
                    {value.cuisineAdventure === 50 && "A good mix of familiar and new flavor combinations"}
                    {value.cuisineAdventure === 75 && "We'll explore beyond your selections while respecting your tastes"}
                    {value.cuisineAdventure === 100 && "Your selections are a starting point for culinary discovery"}
                    {value.cuisineAdventure !== 0 && value.cuisineAdventure !== 25 && value.cuisineAdventure !== 50 && value.cuisineAdventure !== 75 && value.cuisineAdventure !== 100 && "A balanced approach to exploring new flavors"}
                  </Text>
                </Box>
              </Stack>
            )}
          </Stack>
        </Paper>

      {/* Summary */}
      {(value.cuisinePreferences.length > 0 || (value.avoidCuisines || []).length > 0 || value.dislikes.length > 0 || noAvoidCuisines) && (
        <Paper 
          p="lg" 
          radius="lg" 
          bg="gray.0" 
          style={{ 
            border: '1px solid var(--mantine-color-gray-2)',
            minHeight: 80,
            width: '100%'
          }}
        >
          <Group gap="md" align="center" style={{ height: '100%' }}>
            <ThemeIcon 
              size={40} 
              radius="md" 
              color="green" 
              variant="light"
            >
              <IconWorld size={20} />
            </ThemeIcon>
            
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} c="green.7" style={{ wordBreak: 'break-word' }}>
                Taste preferences saved!
              </Text>
              <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
                {value.cuisinePreferences.length > 0 && (
                  <>Loves: {value.cuisinePreferences.join(', ')}</>
                )}
                {value.cuisinePreferences.length > 0 && ((value.avoidCuisines || []).length > 0 || noAvoidCuisines) && <br />}
                {(value.avoidCuisines || []).length > 0 && (
                  <>Avoids cuisines: {(value.avoidCuisines || []).join(', ')}</>
                )}
                {noAvoidCuisines && (
                  <>No cuisines to avoid</>
                )}
                {(value.cuisinePreferences.length > 0 || (value.avoidCuisines || []).length > 0 || noAvoidCuisines) && value.dislikes.length > 0 && <br />}
                {value.dislikes.length > 0 && (
                  <>Avoids foods: {value.dislikes.join(', ')}</>
                )}
                {(value.cuisinePreferences.length > 0 || (value.avoidCuisines || []).length > 0 || noAvoidCuisines || value.dislikes.length > 0) && value.cuisineAdventure !== undefined && <br />}
                {value.cuisineAdventure !== undefined && (
                  <>Adventure level: {
                    value.cuisineAdventure === 0 ? 'Safe' :
                    value.cuisineAdventure === 25 ? 'Familiar' :
                    value.cuisineAdventure === 50 ? 'Balanced' :
                    value.cuisineAdventure === 75 ? 'Adventurous' :
                    value.cuisineAdventure === 100 ? 'Bold' : 'Balanced'
                  }</>
                )}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}

      {/* Navigation */}
        <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
          <Button 
            variant="subtle" 
            size="md"
            leftSection={<IconArrowLeft size={18} />}
            onClick={onBack}
            color="orange"
          >
            Back
          </Button>
          
          <Button
            size="md"
            rightSection={<IconArrowRight size={18} />}
            onClick={handleNext}
            color="orange"
            variant="filled"
          >
            {isLastStep ? 'Finish' : 'Continue'}
          </Button>
        </Group>
    </Stack>
  );
};
